import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { api } from "../lib/api";
import { FolderKanban, Heart, ClipboardList, Calendar, Brain, Plus, FileBarChart, ShieldCheck } from "lucide-react";
import { StatusPill, Eyebrow } from "../components/Bits";

export default function Dashboard() {
  const { user, ready } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);

  useEffect(() => { if (ready && !user) nav("/auth"); }, [ready, user, nav]);
  useEffect(() => { if (user) api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); }, [user]);

  const startProject = async (goal) => {
    const { data } = await api.post("/projects", { goal });
    setProjects((p) => [data, ...p]);
  };

  if (!ready || !user) return <div className="p-16 text-white/50">{t("common.loading")}</div>;

  const icons = [FolderKanban, Heart, ClipboardList, Calendar];
  const labels = t("dashboard.stats");
  const values = [projects.length, 0, 0, 0];
  const goals = t("projects.goals");

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-14" data-testid="dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><Eyebrow>{t("dashboard.eyebrow")}</Eyebrow><h1 className="font-display text-4xl text-white mt-1">{t("dashboard.hello")}, {user.name || ""}.</h1></div>
        <div className="flex items-center gap-2 flex-wrap">
          {user.role === "admin" && (
            <Link to="/admin/pilots" data-testid="admin-pilots-link" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-sm transition-colors duration-200">
              <ShieldCheck size={15} /> {t("admin.review_link")}
            </Link>
          )}
          <Link to="/partners/broker/dashboard" data-testid="broker-dash-quicklink" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/25 text-gold hover:bg-gold/10 text-sm transition-colors duration-200">
            <FileBarChart size={15} /> {t("broker.dash_link")}
          </Link>
          <Link to="/memory" data-testid="memory-link" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-white/80 hover:border-gold/40 text-sm transition-colors duration-200">
            <Brain size={15} /> {t("dashboard.memory_link")}
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        {labels.map((k, i) => {
          const Icon = icons[i];
          return (
            <div key={k} className="glass rounded-2xl p-6">
              <Icon size={18} className="text-gold" />
              <div className="font-display text-3xl text-white mt-3">{values[i]}</div>
              <div className="text-white/50 text-sm">{k}</div>
            </div>
          );
        })}
      </div>

      <h2 className="font-display text-2xl text-white mt-12">{t("dashboard.projects_h")}</h2>

      {projects.length === 0 ? (
        <div className="glass rounded-2xl p-8 mt-4">
          <p className="text-white/60 mb-4">{t("dashboard.empty")}</p>
          <div className="flex flex-wrap gap-2">
            {goals.slice(0, 6).map((g) => (
              <button key={g.goal} onClick={() => startProject(g.goal)} data-testid={`start-project-${g.status}`}
                className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border border-white/10 text-white/80 hover:border-gold/40 hover:text-gold transition-colors duration-200">
                <Plus size={14} /> {g.goal}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-4 mt-4">
          {projects.map((p) => (
            <div key={p.id} data-testid="dashboard-project" className="rounded-2xl bg-surface/60 border border-white/[0.06] p-6">
              <StatusPill status="live" />
              <div className="font-display text-lg text-white mt-3">{p.title}</div>
              <ul className="mt-3 space-y-1.5">{(p.next_actions || []).map((a) => <li key={a} className="text-xs text-white/55 flex gap-2"><span className="text-gold">›</span>{a}</li>)}</ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
